import { useTranslation } from "react-i18next";
import { Copy, MoreHorizontal, Power, PowerOff, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from "@nexus/ui";
import type { PromptDto } from "@/lib/queries";
import { usePermissions, useIamPermission } from "@/features/auth/hooks/usePermissions";
import { usePromptActions } from "@/features/prompts/hooks/usePromptActions";

interface PromptRowActionsProps {
  prompt: PromptDto;
  /** Table rows reveal the trigger on hover; the drawer header keeps it visible. */
  alwaysVisible?: boolean;
  /** The drawer carries its own status switch, so it opts out of this entry. */
  includeStatus?: boolean;
  /** Owned by PromptsPage so dialogs survive the editor sheet dismissing. */
  onDuplicate: (prompt: PromptDto) => void;
  onDelete: (prompt: PromptDto) => void;
}

export function PromptRowActions({
  prompt,
  alwaysVisible = false,
  includeStatus = true,
  onDuplicate,
  onDelete,
}: PromptRowActionsProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const canCreate = useIamPermission(permissions.PROMPTS_CREATE);
  const canUpdate = useIamPermission(permissions.PROMPTS_UPDATE);
  const canDelete = useIamPermission(permissions.PROMPTS_DELETE);
  const { setStatus } = usePromptActions();

  const canToggleStatus = canUpdate && includeStatus;

  // Global prompts are the fallback for their call site: the API refuses to
  // deactivate or delete one, so those entries stay out of reach here.
  const isGlobal = prompt.tenantId === null;
  const isActive = prompt.status === "ACTIVE";

  if (!canCreate && !canToggleStatus && !canDelete) return null;

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("prompts.actions.menuAria", "Actions for {{name}}", {
              name: prompt.name,
            })}
            className={cn(
              "inline-flex size-7 cursor-pointer items-center justify-center rounded-sm text-muted-foreground",
              "transition-[opacity,color] duration-150 ease-out hover:text-foreground",
              "focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring/40",
              "data-[state=open]:opacity-100",
              alwaysVisible ? "opacity-100" : "opacity-0 group-hover:opacity-100",
            )}
          >
            <MoreHorizontal className="size-4" aria-hidden />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-52">
          {canCreate && (
            <DropdownMenuItem className="gap-2" onSelect={() => onDuplicate(prompt)}>
              <Copy className="size-3.5" aria-hidden />
              {t("prompts.actions.duplicate", "Duplicate…")}
            </DropdownMenuItem>
          )}

          {canToggleStatus && (
            <DropdownMenuItem
              className="gap-2"
              disabled={isGlobal}
              onSelect={() =>
                setStatus.mutate({
                  id: prompt.id,
                  status: isActive ? "INACTIVE" : "ACTIVE",
                })
              }
            >
              {isActive ? (
                <PowerOff className="size-3.5" aria-hidden />
              ) : (
                <Power className="size-3.5" aria-hidden />
              )}
              {isActive
                ? t("prompts.actions.deactivate", "Deactivate")
                : t("prompts.actions.activate", "Activate")}
            </DropdownMenuItem>
          )}

          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                className="gap-2"
                disabled={isGlobal}
                onSelect={() => onDelete(prompt)}
              >
                <Trash2 className="size-3.5" aria-hidden />
                {t("prompts.actions.delete", "Delete…")}
              </DropdownMenuItem>
            </>
          )}

          {isGlobal && (canToggleStatus || canDelete) && (
            <p className="px-2 py-1.5 text-[11px] text-muted-foreground">
              {t(
                "prompts.actions.globalLocked",
                "Global prompts are the fallback for their call site.",
              )}
            </p>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
